// Quick entry form for weight events
import React, { useState } from 'react';
import { LocationInput } from './LocationInput';
import { LocationData } from '../types';
import { healthAPI } from '../api';
import './QuickEntry.css';

interface WeightFormProps {
    onSuccess?: (traceId: string) => void;
    onError?: (error: string) => void;
}

export const WeightForm: React.FC<WeightFormProps> = ({ onSuccess, onError }) => {
    const [weight, setWeight] = useState<string>('');
    const [unit, setUnit] = useState<'kg' | 'lbs'>('lbs');
    const [bodyFat, setBodyFat] = useState<string>('');
    const [context, setContext] = useState<string>('');
    const [location, setLocation] = useState<LocationData | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    React.useEffect(() => {
        // Load default unit from profile
        healthAPI.getUserProfile().then(profile => {
            if (profile.settings?.units?.weight) {
                setUnit(profile.settings.units.weight);
            }
        }).catch(console.error);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!weight) {
            if (onError) onError('Please enter a weight');
            return;
        }

        setIsSubmitting(true);

        try {
            const weightNum = parseFloat(weight);
            const data: any = {
                body_fat_pct: bodyFat ? parseFloat(bodyFat) : undefined,
                notes: context  // Send context as notes
            };

            if (location) {
                data.location = location;
            }

            if (unit === 'kg') {
                data.weight_kg = weightNum;
            } else {
                data.weight_lbs = weightNum;
            }

            const requestBody: any = {
                type: 'weight',
                data,
            };

            const response = await healthAPI.createEvent(requestBody);

            // Reset form
            setWeight('');
            setBodyFat('');
            setContext('');

            if (onSuccess) {
                onSuccess(response.trace_id);
            }
        } catch (error) {
            console.error('Error logging weight:', error);
            if (onError) {
                onError(error instanceof Error ? error.message : 'Unknown error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="quick-entry-form weight-form">
            <h3>⚖️ Weight Entry</h3>

            <div className="form-group">
                <label>Weight</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="number"
                        step="0.1"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder={`Enter weight in ${unit}`}
                        className="food-input"
                        style={{ flex: 2 }}
                    />
                    <select
                        value={unit}
                        onChange={(e) => setUnit(e.target.value as 'kg' | 'lbs')}
                        style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid #ddd' }}
                    >
                        <option value="lbs">lbs</option>
                        <option value="kg">kg</option>
                    </select>
                </div>
            </div>

            <div className="form-group">
                <label>Body Fat % (optional)</label>
                <input
                    type="number"
                    step="0.1"
                    value={bodyFat}
                    onChange={(e) => setBodyFat(e.target.value)}
                    placeholder="e.g. 20.5"
                    className="food-input"
                />
            </div>

            <div className="form-group">
                <label>Context (optional)</label>
                <input
                    type="text"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="e.g. 'Yesterday morning', 'Post-workout at gym'"
                    className="food-input"
                />
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                    Mention time (e.g. "8am") or location if you're elsewhere.
                </div>
            </div>

            <LocationInput onLocationChange={setLocation} />

            <button type="submit" className="submit-button" disabled={isSubmitting || !weight}>
                {isSubmitting ? 'Logging...' : 'Log Weight'}
            </button>
        </form>
    );
};
